import { FormStyle, TranslationWidth, WeekDay, formatDate, getLocaleDayNames, getLocaleMonthNames } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, LOCALE_ID, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgbDate,
  NgbDateAdapter,
  NgbDateNativeAdapter,
  NgbDateStruct,
  NgbDatepickerI18n,
  NgbDropdown,
} from '@ng-bootstrap/ng-bootstrap';
import { isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';

export type DatePickerCalendarValue = [Date] | [Date, Date];

const isSameOrAfter = (a, b) => isAfter(a, b) || isSameDay(a, b);
const isSameOrBefore = (a, b) => isBefore(a, b) || isSameDay(a, b);

@Component({
  selector: 'py-date-picker-calendar-input',
  templateUrl: './date-picker-calendar-input.component.html',
  styleUrls: ['./date-picker-calendar-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerCalendarInputComponent),
      multi: true,
    },
    { provide: NgbDateAdapter<Date>, useClass: NgbDateNativeAdapter },
    // ESVCX-6210: Providing custom "implementation" for NgbDatepickerI18n.
    // NgbDatepickerI18nDefault supports internalization but localeId is set once and ignores changes of DynamicLocaleId from I18nModule.
    // As a fix, the methods were copied from default implementation - NgbDatepickerI18nDefault. Here in the custom implementation the DynamicLocaleId is applied correctly and calendar labels are translated after language change.
    { provide: NgbDatepickerI18n, useClass: DatePickerCalendarInputComponent },
  ],
  standalone: false,
})
export class DatePickerCalendarInputComponent extends NgbDatepickerI18n implements ControlValueAccessor {
  private readonly _weekdaysShort: ReadonlyArray<string>;
  private readonly _monthsShort: ReadonlyArray<string>;
  private readonly _monthsFull: ReadonlyArray<string>;

  @Input() min?: Date;
  @Input() max?: Date;

  @Input() disabledDates: Date[] = [];
  @Input() disabledDays: WeekDay[] = [];
  @Input() dateRange = false;

  @Input() dropdown: NgbDropdown;

  disabled = false;

  private value?: DatePickerCalendarValue;
  private onChange?: (value: DatePickerCalendarValue) => void;
  private onTouch?: () => void;
  private hoveredDate: Date | null = null;
  private selectedFromDate: Date | null = null;

  get minAsNgbDate(): NgbDateStruct {
    return (
      this.min && this.ngbDateStructDateAdapter.fromModel(this.max === undefined || this.min <= this.max ? this.min : new Date())
    );
  }

  get maxAsNgbDate(): NgbDateStruct {
    return this.max && (this.min === undefined || this.max >= this.min) && this.ngbDateStructDateAdapter.fromModel(this.max);
  }

  get primaryDateValue(): Date {
    return this.value?.[0];
  }

  get secondaryDateValue(): Date | undefined {
    if (!this.dateRange) {
      return undefined;
    }
    return this.value?.[1];
  }

  private get isConstructingDateRange(): boolean {
    if (!this.dateRange) {
      return false;
    }
    return !!this.selectedFromDate;
  }

  private get from(): Date | undefined {
    if (!this.dateRange) {
      return undefined;
    }
    return this.selectedFromDate || this.primaryDateValue;
  }

  get startDate(): { year: number; month: number } | null {
    if (this.primaryDateValue) {
      return {
        year: this.primaryDateValue.getFullYear(),
        month: this.primaryDateValue.getMonth() + 1, // getMonth() start from 0.
      };
    }

    return null;
  }

  constructor(
    private cd: ChangeDetectorRef,
    private ngbDateStructDateAdapter: NgbDateAdapter<Date>,
    @Inject(LOCALE_ID) protected localeId: string
  ) {
    super();

    const weekdaysStartingOnSunday = getLocaleDayNames(localeId, FormStyle.Standalone, TranslationWidth.Short);
    this._weekdaysShort = weekdaysStartingOnSunday.map((_, index) => weekdaysStartingOnSunday[(index + 1) % 7]);
    this._monthsShort = getLocaleMonthNames(localeId, FormStyle.Standalone, TranslationWidth.Abbreviated);
    this._monthsFull = getLocaleMonthNames(localeId, FormStyle.Standalone, TranslationWidth.Wide);
  }

  writeValue(value: DatePickerCalendarValue): void {
    const isInitialValue = !this.value;
    this.value = value;
    if (isInitialValue) {
      this.cd.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isDateDisabled = (ngbDate: NgbDate): boolean => {
    const targetDate = this.ngbDateToDate(ngbDate);
    const targetWeekDay: WeekDay = targetDate.getDay();
    return (
      this.disabledDays?.includes(targetWeekDay) ||
      this.disabledDates?.some((disabledDate) => isSameDay(disabledDate, targetDate))
    );
  };

  isDateSelected(ngbDate: NgbDate): boolean {
    const targetDate = this.ngbDateToDate(ngbDate);
    const selectedDates: Date[] = this.isConstructingDateRange ? [this.from, this.hoveredDate] : (this.value as Array<Date>);
    return selectedDates?.some((date) => isSameDay(date, targetDate));
  }

  isDateSelectedFrom(ngbDate: NgbDate): boolean {
    const targetDate = this.ngbDateToDate(ngbDate);
    return isSameDay(this.from, targetDate);
  }

  isDateWithinSelection(ngbDate: NgbDate): boolean {
    const date = this.ngbDateToDate(ngbDate);
    return (
      isSameOrAfter(date, this.from) &&
      (this.isConstructingDateRange
        ? this.hoveredDate && isSameOrBefore(date, this.hoveredDate)
        : isSameOrBefore(date, this.secondaryDateValue))
    );
  }

  onDateSelection(ngbDate: NgbDate) {
    if (this.onTouch) {
      this.onTouch();
    }
    const date = this.ngbDateToDate(ngbDate);
    if (this.dateRange) {
      if (this.selectedFromDate && isAfter(date, this.selectedFromDate)) {
        this.updateValue([this.selectedFromDate, date]);
      } else {
        this.selectedFromDate = date;
      }
    } else {
      this.updateValue([date]);
    }
  }

  onDateMouseEnter(ngbDate: NgbDate) {
    this.hoveredDate = this.ngbDateToDate(ngbDate);
  }

  onDateMouseLeave() {
    this.hoveredDate = null;
  }

  private updateValue(value: DatePickerCalendarValue | null) {
    this.value = value;
    this.selectedFromDate = null;
    if (this.onChange) {
      this.onChange(value);
    }
  }

  /* Date adapter returns middle of day rather than start of day */
  private ngbDateToDate(ngbDate: NgbDateStruct): Date {
    return startOfDay(this.ngbDateStructDateAdapter.toModel(ngbDate));
  }

  /* Implementations of abstract methods from NgbDatepickerI18n */
  getWeekdayShortName(weekday: number): string {
    return this._weekdaysShort[weekday - 1] || '';
  }

  getMonthShortName(month: number): string {
    return this._monthsShort[month - 1] || '';
  }

  getMonthFullName(month: number): string {
    return this._monthsFull[month - 1] || '';
  }

  getDayAriaLabel(date: NgbDateStruct): string {
    const jsDate = new Date(date.year, date.month - 1, date.day);
    return formatDate(jsDate, 'fullDate', this.localeId);
  }

  getWeekdayLabel(weekday: number, width?: TranslationWidth): string {
    const weekdaysStartingOnSunday = getLocaleDayNames(
      this.localeId,
      FormStyle.Standalone,
      width === undefined ? TranslationWidth.Short : width
    );
    const weekdays = weekdaysStartingOnSunday.map((_, index) => weekdaysStartingOnSunday[(index + 1) % 7]);
    return weekdays[weekday - 1] || '';
  }
}
